<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <div class="d-flex mb-2 justify-content-end align-items-start">
                    <!-- Title -->
                    <p class="flex-grow-1" v-if="baseNews && baseNews.titles"><b>{{ $t('newsIntro') }}</b> {{baseNews.titles[currentLanguage.key]}}</p>
                    <!-- Language dropdown -->
                    <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                </div>
                <!-- Form -->
                <div class="form-group flex-grow-1 d-flex flex-wrap">
                    <!-- featuredImage -->
                    <div role="group" class="form-group col-12 col-lg-6">
                        <label class=""> {{ $t('featuredImageMandatory') }}</label>
                        <GalleryPicker class="gallery-picker" v-model="news.featuredImage" :ratioWidth="4" :ratioHeight="3" v-bind:class="{ 'is-invalid': genericValidator(news.featuredImage, attemptedSubmit) === false || errorValidator('FeaturedImage') === false }" :max="1"></GalleryPicker>
                    </div>
                    <!-- images -->
                    <div role="group" class="form-group col-12 col-lg-6">
                        <label class=""> {{ $t('images') }}</label>
                        <GalleryPicker class="gallery-picker" v-model="news.images" :ratioWidth="4" :ratioHeight="3" v-bind:class="{ 'is-invalid': errorValidator('Images') === false }" :max="0"></GalleryPicker>
                    </div>
                    <!-- Titles -->
                    <div role="group" class="form-group col-12 col-lg-6">
                        <label class=""> {{ $t('titleMandatory') }}</label>
                        <CInput class="m-0" type="text" v-model="news.titles[currentLanguage.key]" :invalid-feedback="errorOutput['Titles']" :is-valid="genericValidator(news.titles, attemptedSubmit) && errorValidator('Titles')"></CInput>
                    </div>
                    <!-- TagLines -->
                    <div role="group" class="form-group col-12 col-lg-6">
                        <label class=""> {{ $t('tagLinesMandatory') }}</label>
                        <CInput class="m-0" type="text" v-model="news.tagLines[currentLanguage.key]" :invalid-feedback="errorOutput['TagLines']" :is-valid="genericValidator(news.tagLines, attemptedSubmit) && errorValidator('TagLines')"></CInput>
                    </div>
                    <!-- Category -->
                    <div role="group" class="form-group col-12 col-lg-6">
                        <label class=""> {{ $t('categoriesMandatory') }}</label>
                        <OptionsDropdown v-model="news.categories" :options="categoriesOptions" :title="$t('categories')" :index="0" :is-valid="genericValidator(news.categories, attemptedSubmit) && errorValidator('Categories')" :invalid-feedback="errorOutput['Categories']"></OptionsDropdown>
                    </div>
                    <!-- publishDate -->
                    <div role="group" class="form-group col-12 col-lg-6">
                        <label class=""> {{ $t('publishDate') }}</label>
                        <datepicker class="form-control" v-model="news.publishDate" placeholder="DD-MM-YYYY" :disabled-dates="disabledDates"></datepicker>
                    </div>
                    <!-- files (not required) -->
                    <div role="group" class="form-group col-12 col-lg-6">
                        <FilesPicker v-model="news.files" :title="$t('files')" v-bind:class="{'is-invalid': errorValidator('Files') }" :invalidFeedback="errorOutput['Files']"></FilesPicker>
                    </div>
                </div>
                <!-- Content -->
                <div role="group" class="form-group col-12">
                    <label class=""> {{ $t('contentsMandatory') }}</label>
                    <vue-editor v-model="news.contents[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(news.contents, attemptedSubmit) == false || errorValidator('Contents') }"></vue-editor>
                    <div v-if="errorOutput['Contents']" class="invalid-feedback">{{errorOutput['Contents']}}</div>
                </div>
                <!-- Action button + submitting -->  
                <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                <CButton v-else type="button" class="btn btn-primary align-self-end" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                <!-- Loading -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>
 
<script>
import { mapState, mapActions } from 'vuex'
import router from '@/router/index';
import { API_HELPER } from "@/helpers/api.js";
import { deepClone } from "@/helpers/utils.js";
import { CONSTANTS } from '@/helpers/consts';
import { genericValidator, errorValidator } from "@/helpers/utils.js";
import Datepicker from 'vuejs-datepicker';
import InputGetList from "@/classes/InputGetList";
import News from "@/classes/News";
import LanguageDropdown from '@/controls/LanguageDropdown.vue'
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import OptionsDropdown from '@/controls/OptionsDropdown.vue'
import GalleryPicker from '@/controls/GalleryPicker.vue'
import FilesPicker from '@/controls/FilesPicker.vue'
import { VueEditor } from "vue2-editor";
import FormLoading from '@/controls/FormLoading.vue'

  export default {
        components: { LanguageDropdown, ConfirmationPopUp, VueEditor, GalleryPicker, OptionsDropdown, FormLoading, FilesPicker, Datepicker },
    props:{    
        baseNews:
        {
            type: Object,
            default: null,
        },
        baseName:
        {
            type: String,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data()
    {
        return {
            attemptedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            news: new News( {}, {}, {}, []),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage:"",
            disabledDates: {
                to: new Date(new Date().setDate(new Date().getDate()-1))
            },
        }
    },
    mounted: function()
    {
        this.resetNews();
        this.getNewsCategories()
    },
    computed:
    {
        ...mapState('news', ['putNewsOutput']),
        ...mapState('news', ['postNewsOutput']),
        ...mapState('categories', ['getCategoriesOutput']),
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        errorOutput: function()
        {
            var result = this.alertOutput && Array.isArray(this.alertOutput) ? this.alertOutput : [];

            // Validate multi language fields
            for (const [key, value] of Object.entries(this.news.titles)) { if(!value) { result['Titles'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.news.tagLines)) { if(!value) { result['TagLines'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.news.contents)) { if(!value) { result['Contents'] = this.$i18n.t("validateBothTranslations"); break; }; }

            return result;
        },   
        categoriesInfo: function() {
            return this.getCategoriesOutput && this.getCategoriesOutput.data ? this.getCategoriesOutput.data : [];
        },
        categoriesOptions: function()
        {
            var result = [];

            if(this.categoriesInfo)
            {
                this.categoriesInfo.forEach(category => 
                {
                    result.push({key: category.id, label: category.name });
                });
            }

            return result;
        },       
        name: function () {
            return this.baseNews || this.baseName ? (this.baseNews ? this.baseNews.name : this.baseName) : null;
        }
    },
    methods:{
        ...mapActions('categories', ['getCategories']),
        genericValidator,
        errorValidator,
        resetNews(){
            if(!this.baseNews)
            {
                var titles = {}; var tagLines = {}; var contents = {}; 
                CONSTANTS.supportedLanguages.forEach(language => { titles[language.key] = ""; tagLines[language.key] = ""; contents[language.key] = "";  });
                this.news = new News(titles, tagLines, contents, []);
            }
            else
            {
                this.news = deepClone(new News, this.baseNews);  

                // featuredImage
                this.news.featuredImage = {url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, this.news.featuredImagePath]), path: this.news.featuredImagePath};
                
                // Images
                var images = [];
                this.news.imagesPath.forEach(function(file)
                {
                    images.push({url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, file]), path: file});
                });
                this.news.images = images;

                // Files
                var files = [];
                this.news.filesPath.forEach(function(file)
                {
                    files.push({path: file});
                });
                this.news.files = files;

                // Categories
                this.news.categories = this.news.categories.map(x=> x.id);
            }
        },
        getNewsCategories(){
            const itemsPerPage = 999;
            const currentPage = 1;
            const searchText = null;
            const sorter = 1; // ASC
            const propertyName = "name";

            var input = new InputGetList(itemsPerPage, currentPage, searchText, sorter, propertyName);
            input.type = 1; // type -> News

            this.getCategories(input);         
        },
        submitClicked(){
            this.attemptedSubmit = true;

            // Emit event
            if(this.news.isValid())
            {
                this.submitting = true;
                this.$emit('submit-requested', this.news);
            }
        },
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;     
            router.push({name: "News"});
        },
        onProcessTask: function(val)
        {
            if(val != null)
            {
                if(val)
                {
                    this.showConfirmationPopup = true;
                }

                this.submitting = false;
            }
        }
    },
    watch:
    {
        putNewsOutput: function(val)
        {
            this.onProcessTask(val);
        },
        postNewsOutput: function(val)
        {
            this.onProcessTask(val);
        },
        baseNews: function()
        {
            this.resetNews();
        },
        alertOutput: function()
        {
            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        }
    }
  }
</script> 
 
<style scoped>

@media only screen and (max-width: 767px) {
    .main-content {
        flex-direction: column;
    }
}

/deep/.quillWrapper.is-invalid .ql-snow.ql-toolbar,
/deep/.quillWrapper.is-invalid .ql-snow.ql-container
{
    border-color: #e55353;
}
>>>.vdp-datepicker input{
    border: none!important;
}
</style> 